/**
 * Variant Media Grouping
 *
 * Shows every image that belongs to the selected color, not only the single
 * featured image Shopify allows per variant. Images are grouped by a `#ColorName`
 * suffix in their alt text (see the `enable_variant_media_grouping` theme setting).
 *
 * All product media stays in the DOM; this script filters it in place by toggling
 * a hidden class and the `swiper-slide` class, then updates the existing sliders
 * without destroying them (so thumb layout / direction are preserved).
 *
 * Public API (used by the variant picker in global.js):
 *   window.isAltMediaGrouping(sectionId) -> Boolean
 *   window.updateVariantMediaDisplay(sectionId, variant)
 *
 * IMPORTANT: first paint is already filtered server-side by Liquid, and Swiper is
 * initialised from that markup. Init therefore only re-asserts the DOM state and
 * never calls Swiper APIs — doing so before images settle collapses the gallery
 * and can break the surrounding product layout. Swiper is only updated on an
 * actual variant change, when media has loaded, and always inside try/catch.
 */
(function () {
  var HIDDEN_ITEM = "product__media-item--variant-hidden";
  var HIDDEN_SUBITEM = "product__media-subitem--variant-hidden";

  // Mirror of Shopify's `| handle` filter for ASCII values. The alt-text group
  // stored in data-media-group is already handleized in Liquid, so we only need
  // to handleize the raw variant option value here before comparing.
  function handleize(value) {
    return String(value == null ? "" : value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function groupingWrappers(sectionId) {
    var all = document.querySelectorAll(
      '.product__media-wrapper[data-alt-media-grouping="true"]'
    );
    return Array.prototype.filter.call(all, function (wrapper) {
      return wrapperSectionId(wrapper) === sectionId;
    });
  }

  // The desktop wrapper carries data-section="product-media-<id>"; the mobile
  // wrapper does not, so derive the id from a rendered media item instead.
  function wrapperSectionId(wrapper) {
    var el = wrapper.querySelector("[data-media-id], [data-media-sub-id]");
    if (!el) return null;
    var id =
      el.getAttribute("data-media-id") || el.getAttribute("data-media-sub-id");
    if (!id) return null;
    var i = id.lastIndexOf("-");
    return i > -1 ? id.slice(0, i) : id;
  }

  function getSelectedVariant(sectionId) {
    var picker =
      document.querySelector('variant-radios[data-section="' + sectionId + '"]') ||
      document.querySelector('variant-selects[data-section="' + sectionId + '"]');
    if (!picker) return null;
    var script = picker.querySelector("[data-selected-variant]");
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function isHidden(item) {
    return (
      item.classList.contains(HIDDEN_ITEM) ||
      item.classList.contains(HIDDEN_SUBITEM)
    );
  }

  function selectedGroupFor(wrapper, variant) {
    var pos = parseInt(wrapper.getAttribute("data-group-option-position"), 10);
    if (!pos || !variant || !variant.options) return null;
    return handleize(variant.options[pos - 1] || "");
  }

  // Toggle visibility of every media item inside one gallery wrapper (DOM only).
  function filterWrapper(wrapper, selectedGroup) {
    var items = wrapper.querySelectorAll("[data-media-id], [data-media-sub-id]");
    Array.prototype.forEach.call(items, function (item) {
      var group = (item.getAttribute("data-media-group") || "").trim();
      // Shared images (no #tag) stay visible for every color.
      var visible = group === "" || group === selectedGroup;

      var isSub =
        item.hasAttribute("data-media-sub-id") ||
        item.classList.contains("product__media-subitem");
      item.classList.toggle(isSub ? HIDDEN_SUBITEM : HIDDEN_ITEM, !visible);

      // swiper-slide must only be present on visible slider slides.
      var parent = item.parentElement;
      if (parent && parent.classList.contains("swiper-wrapper")) {
        item.classList.toggle("swiper-slide", visible);
      }
    });
  }

  // Hide the thumbnail strip entirely when a color group has one image or fewer.
  function toggleThumbs(wrapper) {
    var subList = wrapper.querySelector(".js-media-sublist");
    if (!subList) return;
    var outer = subList.closest(".product__media-sublist-outer");
    if (!outer) return;
    var visible = subList.querySelectorAll(
      ".product__media-subitem:not(." + HIDDEN_SUBITEM + ")"
    ).length;
    outer.style.display = visible <= 1 ? "none" : "";
  }

  function refreshSwiper(swiper) {
    if (!swiper) return;
    swiper.updateSize();
    swiper.updateSlides();
    swiper.updateProgress();
    swiper.updateSlidesClasses();
    swiper.update();
  }

  // Update the existing sliders in place after a variant change. Never destroys
  // them; preserves each slider's direction. Fully guarded so a Swiper hiccup
  // can never break variant selection or the surrounding page layout.
  function updateSwiper(wrapper, sectionId, variant) {
    try {
      var mainList = wrapper.querySelector(".js-media-list");
      var subList = wrapper.querySelector(".js-media-sublist");
      var mainSwiper = mainList && mainList.swiper;
      var subSwiper = subList && subList.swiper;
      if (!mainSwiper && !subSwiper) return;

      // Remember directions so a stray update() cannot flip horizontal thumbs
      // (under the image) into a vertical side column and vice versa.
      var mainVertical = mainSwiper ? mainSwiper.isVertical() : null;
      var subVertical = subSwiper ? subSwiper.isVertical() : null;

      refreshSwiper(mainSwiper);
      refreshSwiper(subSwiper);

      if (mainSwiper && mainSwiper.isVertical() !== mainVertical) {
        mainSwiper.changeDirection(mainVertical ? "vertical" : "horizontal");
      }
      if (subSwiper && subSwiper.isVertical() !== subVertical) {
        subSwiper.changeDirection(subVertical ? "vertical" : "horizontal");
      }

      if (!mainSwiper || !mainSwiper.slides || !mainSwiper.slides.length) return;

      // Slide to the selected variant's featured image if visible, else first.
      var target = null;
      if (variant && variant.featured_media) {
        var featured = wrapper.querySelector(
          '[data-media-id="' + sectionId + "-" + variant.featured_media.id + '"]'
        );
        if (featured && !isHidden(featured)) target = featured;
      }
      if (!target) target = mainSwiper.slides[0];

      var index = mainSwiper.slides.indexOf(target);
      if (index < 0) return;
      mainSwiper.slideTo(index, 0);
      if (subSwiper) subSwiper.slideTo(index, 0);
      if (typeof mainSwiper.updateAutoHeight === "function") {
        mainSwiper.updateAutoHeight(0);
      }
    } catch (e) {
      /* keep variant selection working even if the slider update fails */
    }
  }

  window.isAltMediaGrouping = function (sectionId) {
    if (!sectionId) return false;
    return groupingWrappers(sectionId).length > 0;
  };

  window.updateVariantMediaDisplay = function (sectionId, variant) {
    if (!sectionId || !variant) return;
    groupingWrappers(sectionId).forEach(function (wrapper) {
      var selectedGroup = selectedGroupFor(wrapper, variant);
      if (selectedGroup === null) return;
      filterWrapper(wrapper, selectedGroup);
      toggleThumbs(wrapper);
      updateSwiper(wrapper, sectionId, variant);
    });
  };

  // Initialise from the variant already selected on the page. Liquid has already
  // rendered the correct filtered markup and Swiper is initialised from it, so
  // this only re-asserts DOM visibility — it does NOT touch Swiper.
  function init(root) {
    try {
      var scope = root && root.querySelectorAll ? root : document;
      var wrappers = scope.querySelectorAll(
        '.product__media-wrapper[data-alt-media-grouping="true"]'
      );
      Array.prototype.forEach.call(wrappers, function (wrapper) {
        var sectionId = wrapperSectionId(wrapper);
        if (!sectionId) return;
        var variant = getSelectedVariant(sectionId);
        if (!variant) return;
        var selectedGroup = selectedGroupFor(wrapper, variant);
        if (selectedGroup === null) return;
        filterWrapper(wrapper, selectedGroup);
        toggleThumbs(wrapper);
      });
    } catch (e) {
      /* never let grouping init interfere with the rest of the page */
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    init(event.target || document);
  });
})();
